import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Loader2, Eye, Save, Unlock } from 'lucide-react';
import { PrioBadge } from './PrioCriteriaPicker';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' }) : '—');
export const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '');

// One break-up letter in the weekly review: status chip, posting deadline, preview/edit — and for
// still-collected letters the "Jetzt freigeben" shortcut (Talu, 10.10.2026).
export function BreakupLetterRow({ l, review, edits, setEdits, busyId, relBusy, onPreview, onSave, onRelease }) {
  const approved = l.status === 'approved';
  const chip = approved
    ? (l.released_early
      ? ['Vorzeitig freigegeben — bereit für QR & iAuto', 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10']
      : ['Auto-freigegeben — bereit für QR & iAuto', 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'])
    : [`Gesammelt — Auto-Freigabe ${fmtDay(review?.next_review_at)} 17:00`, 'text-amber-300 border-amber-500/40 bg-amber-500/10'];
  const edited = edits[l.id] !== undefined && edits[l.id] !== l.body;
  return (
    <div className="px-4 py-3 space-y-2" data-testid={`breakup-review-row-${l.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {l.project_id ? (
          <Link to={`/projects/${l.project_id}`} className="text-xs font-medium text-foreground hover:text-amber-300 transition-colors">
            {l.company_name}
          </Link>
        ) : <span className="text-xs font-medium text-foreground">{l.company_name}</span>}
        <PrioBadge prio={l.prio} testid={`breakup-review-prio-${l.id}`} />
        {l.recipient_name && <span className="text-[11px] text-muted-foreground">→ {l.recipient_name}</span>}
        <span className="text-[10px] text-muted-foreground">erstellt {fmtDate(l.created_at)}</span>
        {l.send_by && (
          <span className={`text-[10px] border rounded-sm px-1.5 py-0.5 font-medium ${l.kpi_due
            ? 'border-red-500/50 bg-red-500/10 text-red-300' : 'border-sky-500/40 bg-sky-500/10 text-sky-300'}`}
            data-testid={`breakup-review-sendby-${l.id}`} title="Versand-Deadline (KPI „Break-up-Briefe“)">
            Versand bis {fmtDay(l.send_by)}
          </span>
        )}
        <span className={`text-[10px] border rounded-sm px-1.5 py-0.5 ${chip[1]}`}
          data-testid={`breakup-review-status-${l.id}`}>{chip[0]}</span>
        <span className="ml-auto flex items-center gap-2">
          {!approved && (
            <Button size="sm" onClick={() => onRelease([l.id])} disabled={relBusy !== ''}
              data-testid={`breakup-review-release-${l.id}`}
              title="Jetzt freigeben — sofort in die Serien-Produktion, ohne auf Freitag zu warten"
              className="h-7 text-[11px] rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
              {relBusy === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />} Jetzt freigeben
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onPreview?.(l)}
            data-testid={`breakup-review-preview-${l.id}`}
            className="h-7 text-[11px] rounded-sm border-border text-sky-300 hover:text-sky-200">
            <Eye className="w-3 h-3" /> Vorschau
          </Button>
          {edited && (
            <Button size="sm" onClick={() => onSave(l)} disabled={busyId === l.id}
              data-testid={`breakup-review-save-${l.id}`}
              className="h-7 text-[11px] rounded-sm bg-sky-600 hover:bg-sky-500 text-white">
              {busyId === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Speichern
            </Button>
          )}
        </span>
      </div>
      <textarea value={edits[l.id] !== undefined ? edits[l.id] : l.body}
        onChange={e => setEdits(prev => ({ ...prev, [l.id]: e.target.value }))}
        rows={Math.min(12, Math.max(5, (l.body || '').split('\n').length + 1))}
        data-testid={`breakup-review-body-${l.id}`}
        className="w-full bg-secondary/20 border border-border rounded-sm p-3 text-xs text-foreground leading-relaxed focus:outline-none focus:border-amber-500/50" />
    </div>
  );
}
